import React, { useEffect, useState } from 'react';
import { array } from 'prop-types';

import { connect } from 'react-redux';

import MaterialTable from 'material-table';
import { Select, MenuItem } from '@material-ui/core';

import ActionBar from '../../../components/ActionBar';
import ActionBarItem from '../../../components/ActionBarItem';
import Switch from '../../../components/FormFields/Switch';

import * as ServiceManagementActions from '../../../actions/serviceManagementActions';

import classes from './RuleSettings.module.css';

const RuleSettings = ({
    isLoading,
    rulesList,
    toggleLoading,
    updateRuleEnabled,
    ...props
}) => {

    const [ruleIds, setRuleIds] = useState([]);
    const [ruleToggles, setRuleToggles] = useState({});
    const [filteredRulesList, setFilteredRulesList] = useState([]);
    const [workbookModeEnabled, setWorkBookModeEnabled] = useState(false);

    useEffect(() => {
        setFilteredRulesList(rulesList);
    }, [rulesList]);

    const bulkRuleAction = isEnabled => {
        const activeRuleIds = [];
        const tempRuleToggles = {...ruleToggles};

        ruleIds.forEach(ruleId => {
            filteredRulesList.forEach(rule => {
                if (rule.id === ruleId) {
                    activeRuleIds.push(ruleId);

                    tempRuleToggles[ruleId] = true;
                }
            });
        });

        setRuleToggles(tempRuleToggles);

        const rulePromises = [];

        activeRuleIds.forEach(ruleId => {
            rulePromises.push(updateRuleEnabled(ruleId, isEnabled));
        });

        Promise.all(rulePromises.map(p => p.catch(err => err))).then(() => {
            activeRuleIds.forEach(ruleId => {
                setRuleToggles({...ruleToggles, [ruleId]: false});
            });
        });
    };

    const faultToServiceCallFilterComponent = ({columnDef, onFilterChanged}) => (
        <Select
            id="enabledSelect"
            style={{width: 100}}
            defaultValue={'all'}
            onChange={(e) => {
                let val;

                switch (e.target.value) {
                    case 'all':
                        val = undefined;
                        break;
                    case 'checked':
                        val = 'checked';
                        break;
                    case 'unchecked':
                        val = 'unchecked';
                        break;
                    default:
                        val = undefined;
                }
        
                onFilterChanged(columnDef.tableData.id, val);                                        
            }}
        >   
            <MenuItem value={'all'}>All</MenuItem>
            <MenuItem value={'checked'}>Enabled</MenuItem>
            <MenuItem value={'unchecked'}>Disabled</MenuItem>
        </Select>
    );

    const handleEnableRules = () => {
        bulkRuleAction(true);
    };

    const handleDisableRules = () => {
        bulkRuleAction(false);
    };

    const handleSingleRule = async (id, isEnabled) => {
        const filteredRules = [...filteredRulesList];

        setRuleToggles({...ruleToggles, [id]: true});
        await updateRuleEnabled(id, !isEnabled);
        setRuleToggles({...ruleToggles, [id]: false});

        rulesList.forEach(rule => {
            filteredRules.forEach(filteredRule => {
                if (rule.id === filteredRule.id) {
                    filteredRule.faultServiceCallsEnabled = rule.faultServiceCallsEnabled;
                }
            });
        });

        setFilteredRulesList(filteredRules);
    };

    const handleRuleToggle = rowData => {
        const ids = rowData.map(row => row.id);

        setRuleIds(ids);
    };

    const renderPriority = priority => {
        switch (priority) {
            case 0:
                return 'Low';
            case 1:
                return 'Medium';
            case 2:
                return 'High';
            default:
                break;
        }
    };

    const renderSwitch = (id, isEnabled) => {
        return (
            <Switch
                checked={isEnabled}
                disableRipple
                name="enabled"
                onChange={() => handleSingleRule(id, isEnabled)}
                isLoading={ruleToggles[id]}
            />
        );
    };

    const toggleWorkbookMode = () => {
        setWorkBookModeEnabled(!workbookModeEnabled);
    };

    return (
        <div className={classes.RuleSettings}>
            <ActionBar>
                <ActionBarItem
                    onClick={toggleWorkbookMode}
                    type="edit"
                >
                    Workbook Mode
                </ActionBarItem>
                <ActionBarItem
                    iconSrc="/assets/images/switchEnabledIconColor.svg"
                    onClick={handleEnableRules}
                >
                    Enable
                </ActionBarItem>
                <ActionBarItem
                    iconSrc="/assets/images/switchDisabledIconColor.svg"
                    onClick={handleDisableRules}
                >
                    Disable
                </ActionBarItem>
            </ActionBar>
            <div className={classes.ruleSettingsTable}>
                <MaterialTable
                    options={{
                        paging:        false,
                        showTitle:     false,
                        search:        false,
                        selection:     true,
                        toolbar:       false,
                        sorting:       true,
                        filtering:     workbookModeEnabled,
                        headerStyle:   { position: 'sticky', top: 0 },
                        maxBodyHeight: 550
                    }}
                    columns={[
                        {
                            title: 'Rule Name',
                            field: 'name'
                        },
                        {
                            title:      'Rule Priority',
                            field:      'priority',
                            render:     rowData => renderPriority(rowData.priority),
                            customSort: (a, b) => a.priority - b.priority,
                            lookup:     { 0: 'Low', 1: 'Medium', 2: 'High'}
                        },
                        {
                            title:                 'Issue Types',
                            field:                 'issueTypes',
                            customFilterAndSearch: (term, rowData) => rowData.issueTypes.filter(t => t.toLowerCase().includes(term.toLowerCase())).length > 0,
                            render:                rowData => rowData.issueTypes.join(', '),
                            sorting:               false
                        },
                        {
                            title:           'Enabled',
                            field:           'faultServiceCallsEnabled',
                            type:            'boolean',
                            render:          rowData => renderSwitch(rowData.id, rowData.faultServiceCallsEnabled),
                            customSort:      (a, b) => a.faultServiceCallsEnabled - b.faultServiceCallsEnabled,
                            filterComponent: faultToServiceCallFilterComponent
                        }
                    ]}
                    data={filteredRulesList}
                    onSelectionChange={handleRuleToggle}
                />
            </div>
        </div>
    );
};

RuleSettings.defaultProps = {
    rulesList: []
};

RuleSettings.propTypes = {
    rulesList: array
};

const mapStateToProps = state => {
    return {
        rulesList: state.svcManagement.rulesList
    };
};

const mapDispatchToProps = {
    ...ServiceManagementActions
};

export default connect(mapStateToProps, mapDispatchToProps)(RuleSettings);
